import {SleepAsync} from "js-vextensions";
import {GetCache} from "../../../Store/main/general/CacheState.js";
import {SessionLog} from "../../../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {nativeBridge} from "../Bridge_Native.js";
import {VideoFileEntry} from "../Bridge_Native/MediaTypes.js";
import {remoteUserProcessesBridge} from "../Bridge_RemoteUserProcesses.js";
import {PeerConnectionData, WebRTCConnectionRequest, WebRTCOfferResponse, WebRTCSignalingMessage, WebRTCSignalingResponse, VideoChunkData, DataChannelMessage_ClientToServer, DataChannelMessage_ServerToClient, VideoRequest} from "./Types.js";
import {ErrToStr} from "../../General/Errors.js";

export class RTCServer {
	private activePeers: Map<string, PeerConnectionData> = new Map();

	SetupBridgeFunctions(): void {
		// Register function for VR client to request WebRTC connection
		remoteUserProcessesBridge.RegisterFunction("RequestRTCConnection", async(request: WebRTCConnectionRequest): Promise<WebRTCOfferResponse>=>{
			SessionLog(`RequestRTCConnection @request.sessionID:${request.sessionId}`);
			return await this.CreatePeerConnection(request);
		});

		// Register function for VR client to send WebRTC signaling data
		remoteUserProcessesBridge.RegisterFunction("HandleWebRTCSignalingFromClient", async(signaling: WebRTCSignalingMessage): Promise<WebRTCSignalingResponse>=>{
			SessionLog(`HandleWebRTCSignaling @signaling.sessionID:${signaling.sessionId}`);
			return await this.HandleWebRTCSignaling(signaling);
		});
	}
	private async HandleWebRTCSignaling(signaling: WebRTCSignalingMessage): Promise<WebRTCSignalingResponse> {
		const peer = this.activePeers.get(signaling.sessionId);
		if (!peer) return {success: false, error: "Session not found"};

		try {
			if (signaling.answer) {
				await peer.peerConnection.setRemoteDescription(signaling.answer.answer);
			} else if (signaling.candidate) {
				await peer.peerConnection.addIceCandidate(signaling.candidate.candidate);
			}
			return {success: true};
		} catch (error) {
			return {success: false, error: ErrToStr(error)};
		}
	}

	private async CreatePeerConnection(request: WebRTCConnectionRequest): Promise<WebRTCOfferResponse> {
		const sessionId = request.sessionId;

		const peerConnection = new RTCPeerConnection({
			iceServers: [{urls: "stun:stun.l.google.com:19302"}]
		});

		const dataChannel = peerConnection.createDataChannel("video", {
			ordered: true,
			//maxPacketLifeTime: 3000
		});

		dataChannel.addEventListener("open", (): void=>{
			console.log("Data channel opened, ready state:", dataChannel.readyState);
			console.log("Data channel config:", {
				ordered: dataChannel.ordered,
				maxPacketLifeTime: dataChannel.maxPacketLifeTime,
				maxRetransmits: dataChannel.maxRetransmits
			});

			dataChannel.addEventListener("message", (event: MessageEvent<string | ArrayBuffer>): void=>{
				if (typeof event.data === "string") {
					try {
						const message = JSON.parse(event.data) as DataChannelMessage_ClientToServer;
						const {videoRequest} = message;
						if (videoRequest) {
							const videoEntry = GetCache().videoFiles.find(a=>a.filePath == videoRequest.videoPath);
							if (!videoEntry) {
								console.error("Video file not found for file-path:", videoRequest.videoPath);
								return;
							}
							this.StartVideoSend(dataChannel, videoRequest, videoEntry);
						}
					} catch (error) {
						console.error("Error parsing video metadata:", error);
					}
				}
			});
		});

		// extra listeners, for debugging
		dataChannel.addEventListener("bufferedamountlow", (): void=>{
			console.log("Buffer amount low:", dataChannel.bufferedAmount);
		});
		dataChannel.addEventListener("closing", (): void=>{
			console.log("Data channel is closing, ready state:", dataChannel.readyState);
			console.log("Peer connection state:", peerConnection.connectionState);
			console.log("ICE connection state:", peerConnection.iceConnectionState);
		});
		dataChannel.addEventListener("close", (): void=>{
			console.log("Data channel closed, ready state:", dataChannel.readyState);
			console.log("Peer connection state:", peerConnection.connectionState);
		});
		dataChannel.addEventListener("error", (error: Event): void=>{
			console.error("Data channel error:", error);
			console.log("Data channel ready state:", dataChannel.readyState);
		});
		// Add peer connection monitoring
		peerConnection.addEventListener("connectionstatechange", (): void=>{
			console.log("Peer connection state changed:", peerConnection.connectionState);
			if (peerConnection.connectionState === "failed" || peerConnection.connectionState === "disconnected") {
				console.log("ICE connection state:", peerConnection.iceConnectionState);
				console.log("ICE gathering state:", peerConnection.iceGatheringState);
			}
		});
		peerConnection.addEventListener("iceconnectionstatechange", (): void=>{
			console.log("ICE connection state changed:", peerConnection.iceConnectionState);
		});

		// Handle ICE candidates - send back to VR client
		peerConnection.addEventListener("icecandidate", async(event: RTCPeerConnectionIceEvent): Promise<void>=>{
			if (event.candidate) {
				await this.SendRTCSignalingToClient({
					sessionId,
					candidate: {candidate: event.candidate},
				});
			}
		});

		const offer = await peerConnection.createOffer();
		await peerConnection.setLocalDescription(offer);

		this.activePeers.set(sessionId, {peerConnection, dataChannel});

		return {
			type: "offer",
			offer: offer,
			sessionId: sessionId
		};
	}
	private async SendRTCSignalingToClient(data: WebRTCSignalingMessage): Promise<void> {
		try {
			const response = await remoteUserProcessesBridge.Call("ReceiveWebRTCSignaling", data) as WebRTCSignalingResponse;
			if (!response.success) {
				console.error("Failed to send signaling to VR client:", response.error);
			}
		} catch (error) {
			console.error("Error sending signaling to VR client:", error);
		}
	}

	private async StartVideoSend(dataChannel: RTCDataChannel, videoRequest: VideoRequest, videoEntry: VideoFileEntry): Promise<void> {
		try {
			const clipData = await this.GetVideoClipDataFromAndroid(videoEntry.contentUri, videoRequest.clipStartTime, videoRequest.clipDuration, videoRequest.audioTrack_preferredLanguage);
			if (clipData.error) {
				console.error("Error getting video chunk:", clipData.error);
				return;
			}

			// Convert base64 to ArrayBuffer once
			const fullData = Base64ToArrayBuffer(clipData.data);
			dataChannel.send(JSON.stringify(new DataChannelMessage_ServerToClient({
				videoStart: {
					videoPath: videoEntry.filePath,
					totalSize: fullData.byteLength, // use actual binary size
				}
			})));

			const chunkSize = 16384;
			let offset = 0;
			while (offset < fullData.byteLength) {
				const chunkEnd = Math.min(offset + chunkSize, fullData.byteLength);
				const isLast = chunkEnd >= fullData.byteLength;

				// Send chunk info
				dataChannel.send(JSON.stringify(new DataChannelMessage_ServerToClient({
					videoChunkInfo: {
						videoPath: videoEntry.filePath,
						offset,
						isLast,
					}
				})));

				// Send actual chunk data
				const chunkData = fullData.slice(offset, chunkEnd);
				dataChannel.send(chunkData);

				offset = chunkEnd;

				// Respect buffering limits
				const maxBufferedAmount = 1024 * 1024; // 1MB buffer limit
				while (dataChannel.bufferedAmount > maxBufferedAmount) {
					await SleepAsync(10);
				}
			}
		} catch (error) {
			console.error("Error streaming video:", error);
		}
	}

	private async GetVideoClipDataFromAndroid(contentUri: string, clipStartTime: number, clipDuration: number, audioTrack_preferredLanguage: string): Promise<VideoChunkData> {
		const result = await nativeBridge.Call("GetVideoClipData", {contentUri, clipStartTime, clipDuration, audioTrack_preferredLanguage});
		return result as VideoChunkData;
	}
}

function Base64ToArrayBuffer(base64: string): ArrayBuffer {
	const binaryString = atob(base64);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes.buffer;
}

export const rtcServer = new RTCServer();
export function InitRTCServer() {
	rtcServer.SetupBridgeFunctions();
}